import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, ArrowDown2, ArrowUp2, Trash } from "iconsax-react";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { EmptyState, FileUploader } from "@/shared/components";
import { useAppSelector, useDocumentSuggest } from "@/shared/hooks";
import { Button, ToggleButton, Toast } from "@/shared/ui";
import type { EmploymentRecordItem } from "@/shared/api/documentSuggest";
import {
  useCreateExperienceMutation,
  useDeleteExperienceMutation,
  useGetExperienceQuery,
  useGetWorkProofQuery,
  useSetExperienceFlagMutation,
  useSubmitWorkProofMutation,
} from "./api";
import ExperienceForm from "./ExperienceForm";
import ExperienceListSkeleton from "./ExperienceListSkeleton";
import type { ExperienceRecord } from "./types";
import { monthYearKey } from "./utils";

interface Props {
  token: string;
  onNext: () => void;
}

function recordDedupKey(organizationName: string, beginningDate: string | null): string {
  return `${organizationName.trim().toLowerCase()}|${monthYearKey(beginningDate)}`;
}

export default function ExperienceList({ token, onNext }: Props) {
  const { t } = useTranslation("ApplyApplicationPage");

  const { data, isLoading, refetch } = useGetExperienceQuery(token);
  const [createExperience] = useCreateExperienceMutation();
  const [deleteExperience, deleteState] = useDeleteExperienceMutation();
  const [setExperienceFlag, flagState] = useSetExperienceFlagMutation();
  const [submitWorkProof] = useSubmitWorkProofMutation();
  const { data: workProofData } = useGetWorkProofQuery(token);
  const { scan: scanLaborBook, state: ocrState, reset: resetOcr } = useDocumentSuggest(token);

  const flagFromServer = useAppSelector((s) => s.completeness.data?.sections.experience?.has_no_work_experience);

  const list = data ?? [];
  const [openIds, setOpenIds] = useState<Set<number>>(() => new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [hasNoExperience, setHasNoExperience] = useState(flagFromServer ?? false);
  const [workProofFile, setWorkProofFile] = useState<File | null>(null);
  const [laborBookRemoved, setLaborBookRemoved] = useState(false);

  const [toast, setToast] = useState<{
    text: string;
    color: "positive" | "negative" | "notice" | "grey";
  } | null>(null);
  const [ocrToast, setOcrToast] = useState<{ color: "positive" | "notice"; text: string } | null>(null);

  const isScanning = ocrState === "scanning";

  function toggleOpen(id: number) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function closeItem(id: number) {
    setOpenIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  async function handleFlagChange(checked: boolean) {
    setHasNoExperience(checked);
    setIsAdding(false);
    setOpenIds(new Set());
    try {
      await setExperienceFlag({ token, has_no_work_experience: checked }).unwrap();
    } catch {
      setHasNoExperience(!checked);
      setToast({ color: "negative", text: "experience.flag_error" });
    }
  }

  async function runLaborBookOcr(file: File) {
    const result = await scanLaborBook(file, "employment_record_book");
    if (result.status === "idle") return;
    const items = (result.items ?? []) as EmploymentRecordItem[];
    if (items.length === 0) {
      setOcrToast({ color: "notice", text: "experience.ocr.wrongFile" });
      return;
    }

    const seen = new Set(list.map((r) => recordDedupKey(r.organization_name, r.beginning_date)));
    let added = 0;
    for (const item of items) {
      const key = recordDedupKey(item.employer_name ?? "", item.start_date ?? "");
      if (seen.has(key)) continue;
      seen.add(key);
      try {
        await createExperience({
          token,
          body: {
            organization_name: item.employer_name ?? "",
            work_position: item.position ?? "",
            beginning_date: item.start_date ?? "",
            end_date: item.is_current ? null : item.end_date,
            is_current: item.is_current,
          },
        }).unwrap();
        added += 1;
      } catch {
        setOcrToast({ color: "notice", text: "experience.ocr.error" });
      }
    }

    const refreshed = await refetch();
    setOpenIds(new Set((refreshed.data ?? []).map((r) => r.id)));

    setOcrToast(
      added > 0
        ? { color: "positive", text: "experience.ocr.filled" }
        : { color: "notice", text: "experience.ocr.alreadyAdded" },
    );
  }

  async function handleLaborBookChange(file: File | null) {
    setWorkProofFile(file);
    setOcrToast(null);
    if (!file) {
      resetOcr();
      return;
    }
    setLaborBookRemoved(false);
    try {
      await submitWorkProof({ token, file }).unwrap();
    } catch {
      setToast({ color: "negative", text: "experience.proof.toast.error" });
    }
    await runLaborBookOcr(file);
  }

  async function handleDelete(id: number) {
    try {
      await deleteExperience({ token, id }).unwrap();
      closeItem(id);
      setToast({ color: "positive", text: "experience.toast.delete_success" });
    } catch {
      setToast({ color: "negative", text: "experience.toast.delete_error" });
    }
  }

  function renderHeader(item: ExperienceRecord): string {
    const title = [item.organization_name, item.work_position].filter(Boolean).join(" · ");
    return title || t("experience.work_title");
  }

  if (isLoading) {
    return <ExperienceListSkeleton />;
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex justify-between items-center">
        <h2 className="text-display-2xs content-base-primary">{t("experience.title")}</h2>

        {!hasNoExperience && (
          <Button
            variant="tertiary"
            size="lg"
            disabled={isAdding || isScanning}
            onClick={() => {
              setIsAdding(true);
              setOpenIds(new Set());
            }}
            className="self-start flex items-center gap-2">
            {t("experience.add_experience")}
            <Add size={20} color="currentColor" />
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 pb-4 border-b surface-base-stroke">
        <span className="text-body-regular-md content-base-secondary">{t("experience.no_experience_label")}</span>
        <ToggleButton checked={hasNoExperience} onChange={handleFlagChange} disabled={flagState.isLoading} />
      </div>

      {hasNoExperience ? (
        <p className="text-body-regular-sm content-base-secondary p-3 surface-secondary-fill radius-sm">
          {t("experience.no_experience_note")}
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          <FileUploader
            label={t("experience.labor_book.label")}
            value={workProofFile}
            existingFileUrl={laborBookRemoved ? null : (workProofData?.work_proof_file_url ?? null)}
            onChange={handleLaborBookChange}
            onRemoveExisting={() => setLaborBookRemoved(true)}
            maxSizeMB={50}
          />

          <Toast color="grey" text={t("experience.labor_book.hint")} closable={false} autoClose={false} isFullWidth />

          {isScanning && (
            <Toast
              color="grey"
              text={t("experience.ocr.scanning")}
              closable={false}
              autoClose={false}
              isFullWidth
            />
          )}

          {ocrToast && (
            <Toast
              key={`${ocrToast.color}-${ocrToast.text}`}
              color={ocrToast.color}
              text={t(ocrToast.text)}
              isFullWidth
              onClose={() => setOcrToast(null)}
            />
          )}

          {list.map((item) => {
            const isOpen = openIds.has(item.id);
            return (
              <div key={item.id} className="flex flex-col gap-4">
                <div className="flex justify-between items-center border-b pb-4 surface-base-stroke">
                  <button
                    type="button"
                    className="flex justify-between items-center flex-1 cursor-pointer gap-3"
                    onClick={() => toggleOpen(item.id)}>
                    <p className="text-body-bold-lg content-base-primary text-left">{renderHeader(item)}</p>

                    <div className="flex items-center gap-2">
                      <span className="input-box-shadow radius-2xs w-6 h-6 flex items-center justify-center content-base-secondary">
                        {isOpen ? (
                          <ArrowUp2 size={16} color="currentColor" />
                        ) : (
                          <ArrowDown2 size={16} color="currentColor" />
                        )}
                      </span>
                      <Button
                        variant="destructive"
                        isIconButton
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        disabled={deleteState.isLoading}
                        className="w-6! aspect-square! rounded-[4px]! p-0!">
                        <Trash size={16} color="currentColor" />
                      </Button>
                    </div>
                  </button>
                </div>

                {isOpen && (
                  <ExperienceForm
                    token={token}
                    record={item}
                    onCancel={() => closeItem(item.id)}
                    onSuccess={() => closeItem(item.id)}
                    onToast={(next) => setToast(next)}
                  />
                )}
              </div>
            );
          })}

          {list.length === 0 && !isAdding && !isScanning && (
            <EmptyState compact noDataText={t("experience.empty")} noDataHint={t("experience.emptyHint")} />
          )}

          {isAdding && (
            <div className="flex flex-col gap-6 pt-2">
              <ExperienceForm
                token={token}
                onCancel={() => setIsAdding(false)}
                onSuccess={() => setIsAdding(false)}
                onToast={(next) => setToast(next)}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button variant="primary" size="lg" type="button" onClick={onNext} disabled={isScanning}>
          {t("experience.next_section")}
          <span className="text-white">
            <ArrowRightIcon />
          </span>
        </Button>
      </div>

      {toast && (
        <Toast
          key={`${toast.color}-${toast.text}`}
          color={toast.color}
          text={t(toast.text)}
          isFullWidth
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
